import React, {useRef} from "react";
import { Typography, CircularProgress } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { View } from "react-native";
import UserAutocomplete from "../../components/Input/UserAutocomplete";
import CustomDateRangePicker from "../../components/Input/CustomDateRangePicker";
import ReportActions from "../../components/actions/ReportActions";
import SelectedUserCard from "../../components/Cards/SelectedUserCard";
import ReportTable from "../../components/tables/ReportTable";
import { useReport } from "../../hooks/TimeTracking/useReport";
import { AppUser } from "../../services/TimeTracking/types";
import ScreenContainer from "../../components/Containers/ScreenContainer";
import Notification from "../../components/Notification/Notification";
import RequestEntrySheet, {
  RequestEntrySheetRef,
} from "../../components/Request/RequestEntrySheet";
import dayjs from "dayjs";



const ReportScreen: React.FC = () => {
  const [notificationMessage, setNotificationMessage] = React.useState<string | null>(null);
  const {
    users,
    selectedUser,
    setSelectedUser,
    dateRange,
    setDateRange,
    report,
    generateReport,
    download,
    loading,
    errorNotification,
    dismissNotification,
  } = useReport();
 
  const requestSheetRef = useRef<RequestEntrySheetRef>(null); 
  const handleEditEntry = () => {
    const now = dayjs(); // Hora y fecha actual
    const type = "ClockIn";
    const id = undefined;
  
    const fullTimestamp = `${now.format("YYYY-MM-DD")} ${now.format("hh:mm A")}`;
    const parsedDate = dayjs(fullTimestamp, "YYYY-MM-DD hh:mm A");
  
    if (!parsedDate.isValid()) {
      console.warn("❌ Invalid date:", { type, time: fullTimestamp, id });
      return;
    }
  
    console.log("📋 Creating new default entry:", { type, parsedDate: parsedDate.toISOString() });
  
    requestSheetRef.current?.show(type, parsedDate.toDate(), id);
  };
  return (
    <ScreenContainer>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <View className="bg-white p-4 sm:p-6 md:p-8 rounded-md shadow-sm">
          <Typography variant="h4" color="primary" gutterBottom>
            Report
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Here you can view the time tracking report for a specific user and date
            range. Select a user and date range to generate the report.
          </Typography>

          <View className="flex flex-col lg:flex-row flex-wrap gap-4 my-6 w-full">
            {users.length > 0 && (
              <View className="flex-1">
                <UserAutocomplete
                  users={users}
                  selectedUser={selectedUser}
                  onSelect={(user) => setSelectedUser(user as AppUser)}  
                />
              </View>
            )}

            <View className="flex-1 min-w-[250px]">
              <CustomDateRangePicker value={dateRange} onChange={setDateRange} />
            </View>

            <View className="flex-1 min-w-[250px]">
              <ReportActions
                onGenerate={generateReport}
                onDownload={() => download(report)}
                onRequest={() => handleEditEntry()}
                onDelete={() => {
                  throw new Error("Delete not implemented.");
                }}
                canDelete={false}
              />
            </View>
            
          </View>

          {selectedUser && <SelectedUserCard user={selectedUser} />}
          {loading && <CircularProgress sx={{ mt: 2 }} />}

          {report ? (
            <ReportTable 
            data={report} 
            onNotify={(msg: string) => setNotificationMessage(msg)}
            />
          ) : (
            <Typography sx={{ mt: 2, fontStyle: "italic" }}>
              The search has not been performed yet.
            </Typography>
          )}
        </View>
      </LocalizationProvider>
      {notificationMessage && (
        <Notification
          title="Notification!"
          message={notificationMessage}
          type="info"
          actions={
            <button
              onClick={() => setNotificationMessage(null)}
              className="text-sm font-medium text-white bg-blue-600 px-3 py-1 rounded hover:bg-blue-700"
            >
              Close
            </button>
          }
        />
      )}

      <RequestEntrySheet
        ref={requestSheetRef}
        onSubmit={(data) => {
          console.log("✅ Submitted Correction:", data);
        }}
      />
    </ScreenContainer>
  );
};

export default ReportScreen;
